import React from 'react';

import EmptyGlossarySection from '../../glossary/EmptyGlossarySection';
import GlossaryEntitiesList from '../../glossary/GlossaryEntitiesList';
import GlossaryActions from './GlossaryActions';

import { EntityType, GlossaryNode, GlossaryTerm } from '../../../types.generated';
import { useBatchRemoveEntitiesMutation } from '../../../graphql/mutations.generated';

import { bulkDeleteGlossaryEntities } from './utils/bulkDeleteGlossaryEntities';
import { useEntityData, useRefetch } from '../shared/EntityContext';
import { useEntityRegistry } from '../../useEntityRegistry';
import { usePagination } from '../../shared/usePagination';
import { sortGlossaryTerms } from '../glossaryTerm/utils';
import { sortGlossaryNodes } from './utils';
import { ENTITY_ACTIONS } from '../shared/rbacConstantsService';
import { buildGlossaryBulkSelectHandlers } from '../../glossary/utils';
import { useRbac } from '../shared/useRbac';

function ChildrenTab() {
    const { entityData } = useEntityData();
    const entityRegistry = useEntityRegistry();
    const { canPerformEntityMenuAction } = useRbac();

    if (!entityData) return <></>;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [batchRemoveGlossaryMutation] = useBatchRemoveEntitiesMutation();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const refetch = useRefetch();

    const childNodes = (entityData?.children?.relationships || [])
        .filter((child) => child?.entity?.type === EntityType.GlossaryNode)
        .sort((nodeA, nodeB) => sortGlossaryNodes(entityRegistry, nodeA.entity, nodeB.entity))
        .map((child) => child.entity);
    const childTerms = (entityData?.children?.relationships || [])
        .filter((child) => child?.entity?.type === EntityType.GlossaryTerm)
        .sort((termA, termB) => sortGlossaryTerms(entityRegistry, termA.entity, termB.entity))
        .map((child) => child.entity);

    const combinedChildren = [...((childNodes || []) as GlossaryNode[]), ...((childTerms || []) as GlossaryTerm[])];

    const hasTermsOrNodes = !!childNodes?.length || !!childTerms?.length;

    const getPrivileges = (action, entityType: EntityType) => {
        return canPerformEntityMenuAction(entityType, action);
    };

    const canExport = getPrivileges(ENTITY_ACTIONS.EXPORT, EntityType.GlossaryTerm);
    const canDeleteTerm = getPrivileges(ENTITY_ACTIONS.DELETE, EntityType.GlossaryTerm);
    const canDeleteNode = getPrivileges(ENTITY_ACTIONS.DELETE, EntityType.GlossaryNode);

    const {
        page,
        pageSize,
        selectedItems,
        setSelectedItems,
        handleChangePage,
        handleShowSizeChange,
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = usePagination({
        items: combinedChildren.map((child) => child.urn || ''),
    });

    const { handleSelectAllSelectable, isAllSelectableSelected, isSelectableIndeterminate } =
        buildGlossaryBulkSelectHandlers({
            terms: (childTerms as GlossaryTerm[]) || [],
            nodes: (childNodes as GlossaryNode[]) || [],
            canInteractWithTerm: canDeleteTerm || canExport,
            canInteractWithNode: canDeleteNode || canExport,
            page,
            pageSize,
            selectedItems,
            setSelectedItems,
        });

    /**
     * Handles the bulk deletion of the selected glossary terms.
     */
    const handleBulkDelete = () => {
        bulkDeleteGlossaryEntities({
            nodes: childNodes as GlossaryNode[],
            terms: childTerms as GlossaryTerm[],
            selectedItems,
            batchRemoveGlossaryMutation,
            setSelectedItems,
            refetch,
            onSuccess: () => {
                // Reset the page to the first page after deleting the entities.
                handleChangePage(1);
            },
        });
    };

    if (hasTermsOrNodes) {
        return (
            <>
                <GlossaryActions
                    canExport={canExport}
                    canDeleteTerm={canDeleteTerm}
                    canDeleteNode={canDeleteNode}
                    selectedItems={selectedItems}
                    nodes={(childNodes as GlossaryNode[]) || []}
                    terms={(childTerms as GlossaryTerm[]) || []}
                    setSelectedItems={setSelectedItems}
                    handleBulkDelete={handleBulkDelete}
                    isAllSelected={isAllSelectableSelected}
                    isIndeterminate={isSelectableIndeterminate}
                    handleSelectAll={handleSelectAllSelectable}
                />
                <GlossaryEntitiesList
                    page={page}
                    pageSize={pageSize}
                    canExport={canExport}
                    canDeleteTerm={canDeleteTerm}
                    canDeleteNode={canDeleteNode}
                    selectedItems={selectedItems}
                    nodes={(childNodes as GlossaryNode[]) || []}
                    terms={(childTerms as GlossaryTerm[]) || []}
                    onChangePage={handleChangePage}
                    setSelectedItems={setSelectedItems}
                    onShowSizeChange={handleShowSizeChange}
                />
            </>
        );
    }

    return <EmptyGlossarySection description="No Terms or Term Groups" />;
}

export default ChildrenTab;
